interface OrderItem {
  name: string;
  quantity: number;
  points: number;
  spec?: string;
  image?: string;
}

interface OrderAddress {
  name?: string;
  phone?: string;
  detail?: string;
}

interface OrderRow {
  id: string;
  order_no: string;
  user_id: string;
  items: OrderItem[];
  total_amount: number;
  address: OrderAddress | null;
  status: string;
  created_at: string;
  logistics_company?: string;
  tracking_no?: string;
  shipped_at?: string;
  custom_users?: { nickname: string | null; phone: string | null } | null;
}

interface OrderDetailModalProps {
  order: OrderRow;
  onClose: () => void;
  onShip?: (order: OrderRow) => void;
}

const statusStyle: Record<string, { bg: string; text: string; dot: string }> = {
  '待付款': { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  '待发货': { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-400' },
  '待收货': { bg: 'bg-sky-50', text: 'text-sky-600', dot: 'bg-sky-400' },
  '已完成': { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-400' },
  '已取消': { bg: 'bg-gray-100', text: 'text-gray-400', dot: 'bg-gray-300' },
};

const OrderDetailModal = ({ order, onClose, onShip }: OrderDetailModalProps) => {
  const style = statusStyle[order.status] ?? statusStyle['待付款'];
  const items: OrderItem[] = Array.isArray(order.items) ? order.items : [];

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col"
          style={{ animation: 'slideUp 0.2s ease' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="font-bold text-gray-900 text-base">订单详情</h2>
              <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${style.bg} ${style.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                {order.status}
              </span>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

            {/* 订单信息 */}
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">订单信息</h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                <Row label="订单号" value={<span className="font-mono text-gray-700 text-sm">{order.order_no}</span>} />
                <Row label="下单时间" value={formatTime(order.created_at)} />
                <Row label="订单状态" value={
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>{order.status}</span>
                } />
                {order.shipped_at && (
                  <Row label="发货时间" value={formatTime(order.shipped_at)} />
                )}
              </div>
            </section>

            {/* 会员信息 */}
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">会员信息</h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                <Row label="昵称" value={order.custom_users?.nickname ?? '未知用户'} />
                <Row label="手机号" value={order.custom_users?.phone ?? '-'} />
                <Row label="用户 ID" value={<span className="font-mono text-xs text-gray-500 break-all">{order.user_id}</span>} />
              </div>
            </section>

            {/* 收货地址 */}
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">收货地址</h3>
              {order.address ? (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                  <Row label="收货人" value={order.address.name ?? '-'} />
                  <Row label="联系电话" value={order.address.phone ?? '-'} />
                  <Row label="详细地址" value={order.address.detail ?? '-'} />
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-400">暂无收货地址</div>
              )}
            </section>

            {/* 物流信息 */}
            {(order.logistics_company || order.tracking_no) && (
              <section>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">物流信息</h3>
                <div className="bg-sky-50 rounded-xl p-4 space-y-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-truck-line text-sky-500 text-base" />
                    <span className="text-sm font-semibold text-sky-700">已发货</span>
                  </div>
                  <Row label="物流公司" value={<span className="font-medium text-gray-700">{order.logistics_company ?? '-'}</span>} />
                  <Row label="快递单号" value={<span className="font-mono text-gray-700 text-sm">{order.tracking_no ?? '-'}</span>} />
                  {order.shipped_at && <Row label="发货时间" value={formatTime(order.shipped_at)} />}
                </div>
              </section>
            )}

            {/* 商品明细 */}
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">商品明细</h3>
              <div className="bg-gray-50 rounded-xl overflow-hidden">
                {items.length === 0 ? (
                  <p className="p-4 text-sm text-gray-400">暂无商品信息</p>
                ) : (
                  <>
                    <div className="divide-y divide-gray-100">
                      {items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-4 py-3">
                          <div className="w-12 h-12 rounded-xl bg-gray-200 flex-shrink-0 flex items-center justify-center overflow-hidden">
                            {item.image ? (
                              <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                            ) : (
                              <i className="ri-shopping-bag-line text-gray-400 text-base" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 font-medium truncate">{item.name ?? '未知商品'}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{item.spec ?? '默认规格'} · x{item.quantity ?? 1}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-emerald-600">{((item.points ?? 0) * (item.quantity ?? 1)).toLocaleString()}</p>
                            <p className="text-xs text-gray-400">积分</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 px-4 py-3 flex justify-between items-center">
                      <span className="text-sm text-gray-500">共消耗积分</span>
                      <div>
                        <span className="text-emerald-600 font-bold text-lg">{Number(order.total_amount).toLocaleString()}</span>
                        <span className="text-emerald-500 text-xs ml-0.5">积分</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex gap-3">
            {order.status === '待发货' && onShip && (
              <button
                onClick={() => onShip(order)}
                className="flex-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors"
              >
                <i className="ri-truck-line mr-1.5" />立即发货
              </button>
            )}
            <button
              onClick={onClose}
              className={`border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors ${order.status === '待发货' && onShip ? '' : 'flex-1'}`}
              style={{ flex: order.status === '待发货' && onShip ? '0 0 auto' : undefined, paddingLeft: 20, paddingRight: 20 }}
            >
              关闭
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </>
  );
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-sm text-gray-400 flex-shrink-0 w-20">{label}</span>
    <span className="text-sm text-gray-700 text-right flex-1">{value}</span>
  </div>
);

export default OrderDetailModal;
